/**
 * /student — the student's exam list.
 *
 * `can_start` and `blocked_reason` come from the server and encode exactly the
 * guards `POST /api/attempts` enforces, so the button state and the explanation
 * cannot drift from what the API will actually allow. `blocked_reason` is shown
 * verbatim; it is written for the student to read.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BarChart3, TrendingUp } from "lucide-react";
import type {
  AttemptStatus,
  MyExamsResponse,
  StudentExamSummary,
} from "@exam/shared";
import { AppShell } from "@/components/AppShell";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { listMyExams } from "@/lib/exam-api";

export function StudentHomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState<MyExamsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listMyExams();
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load your exams.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AppShell title="My exams">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h1 className="text-xl font-semibold text-ink">My exams</h1>
          {/* The only route into the progress page: this list shows one paper
              per row, so "how am I doing overall?" has to be answered
              elsewhere. */}
          <Link
            to="/student/progress"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline"
          >
            <TrendingUp className="size-3.5" />
            My progress
          </Link>
        </div>

        {error ? (
          <Card>
            <CardBody>
              <p className="text-xs text-violation">{error}</p>
            </CardBody>
          </Card>
        ) : null}

        <Card>
          <CardHeader title="Upcoming" />
          {loading ? (
            <CardBody>
              <p className="text-xs text-ink-faint">Loading…</p>
            </CardBody>
          ) : (
            <UpcomingTable
              exams={data?.upcoming ?? []}
              onStart={(exam) => navigate(`/student/exam/${exam.id}/setup`)}
            />
          )}
        </Card>

        <Card>
          <CardHeader title="Past" />
          {loading ? (
            <CardBody>
              <p className="text-xs text-ink-faint">Loading…</p>
            </CardBody>
          ) : (
            <PastTable exams={data?.past ?? []} />
          )}
        </Card>
      </div>
    </AppShell>
  );
}

function UpcomingTable({
  exams,
  onStart,
}: {
  exams: StudentExamSummary[];
  onStart: (exam: StudentExamSummary) => void;
}) {
  if (exams.length === 0) {
    return (
      <CardBody>
        <p className="text-xs text-ink-faint">No exams are scheduled.</p>
      </CardBody>
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Exam</TH>
          <TH>Subject</TH>
          <TH>Scheduled</TH>
          <TH align="right">Duration</TH>
          <TH align="right">Marks</TH>
          <TH align="right">{""}</TH>
        </TR>
      </THead>
      <TBody>
        {exams.map((exam) => (
          <TR key={exam.id}>
            <TD>
              <span className="font-medium text-ink">{exam.title}</span>
              {!exam.can_start && exam.blocked_reason ? (
                <span className="mt-0.5 block text-2xs text-ink-faint">
                  {exam.blocked_reason}
                </span>
              ) : null}
              <SyllabusTopics topics={exam.syllabus_topics} />
            </TD>
            <TD>{exam.subject_name}</TD>
            <TD>{formatWhen(exam.scheduled_at)}</TD>
            <TD align="right">
              <span className="tabular">{exam.duration_min}</span> min
            </TD>
            <TD align="right">
              <span className="tabular">{exam.total_marks}</span>
            </TD>
            <TD align="right">
              <Button
                size="sm"
                variant={exam.can_start ? "primary" : "secondary"}
                icon={<ArrowRight />}
                disabled={!exam.can_start}
                onClick={() => onStart(exam)}
              >
                {exam.attempt?.status === "in_progress" ? "Resume" : "Start"}
              </Button>
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

/**
 * What this exam covers, when the exam publishes it.
 *
 * The point is to make "revise for the exam" mean something on the days
 * BEFORE it: the row was otherwise a title, a date and a mark total. Shown
 * inline rather than behind a click, because a student who has to go looking
 * for the syllabus will not find it.
 *
 * Names only, and deliberately nothing beneath them — no counts per topic, no
 * difficulty, no stems. A topic list says "revise loops and string
 * concatenation"; anything finer starts telling the student where the marks
 * are, and then they are studying the paper instead of the subject.
 */
/**
 * Enough to scan at a glance, with the rest a click away.
 *
 * A real paper covers about twenty topics at this grain, and twenty chips in a
 * table cell is a wall rather than a summary — the student reads none of it.
 * Eight is roughly what fits on one line at normal widths, and the count on
 * the button means the list never pretends to be shorter than it is.
 */
const TOPIC_PREVIEW = 8;

function SyllabusTopics({ topics }: { topics: string[] | null }) {
  const [expanded, setExpanded] = useState(false);
  // Null means the exam does not publish its syllabus, which is the default
  // and is not something to explain on every row. Empty means it does publish
  // and the paper names no topics — a content problem, and saying so is more
  // useful to everyone than the same blank space.
  if (topics === null) return null;
  if (topics.length === 0) {
    return (
      <span className="mt-1 block text-2xs text-ink-faint">
        Topics for this exam have not been listed yet.
      </span>
    );
  }

  const shown = expanded ? topics : topics.slice(0, TOPIC_PREVIEW);
  const hidden = topics.length - shown.length;

  return (
    <span className="mt-1.5 block">
      <span className="text-2xs text-ink-faint">Covers</span>
      <span className="mt-1 flex flex-wrap items-center gap-1">
        {shown.map((topic) => (
          <span
            key={topic}
            className="rounded-sm border border-border bg-surface-sunken px-1.5 py-0.5 text-2xs text-ink-muted"
          >
            {topic}
          </span>
        ))}
        {hidden > 0 || expanded ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded-sm px-1 py-0.5 text-2xs font-medium text-accent hover:underline"
          >
            {hidden > 0 ? `+${hidden} more` : "Show fewer"}
          </button>
        ) : null}
      </span>
    </span>
  );
}

function PastTable({ exams }: { exams: StudentExamSummary[] }) {
  if (exams.length === 0) {
    return (
      <CardBody>
        <p className="text-xs text-ink-faint">
          You have not completed any exams yet.
        </p>
      </CardBody>
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Exam</TH>
          <TH>Subject</TH>
          <TH>Submitted</TH>
          <TH align="right">Score</TH>
          <TH>Status</TH>
          <TH align="right">{""}</TH>
        </TR>
      </THead>
      <TBody>
        {exams.map((exam) => {
          const score = exam.attempt?.score ?? null;
          const max = exam.attempt?.max_score ?? null;
          const pct =
            score !== null && max !== null && max > 0
              ? Math.round((score / max) * 100)
              : null;
          return (
            <TR key={exam.id}>
              <TD>
                <span className="font-medium text-ink">{exam.title}</span>
              </TD>
              <TD>{exam.subject_name}</TD>
              <TD>
                {exam.attempt?.submitted_at != null ? (
                  formatWhen(exam.attempt.submitted_at)
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </TD>
              <TD align="right">
                {score !== null ? (
                  <span className="tabular">
                    {score} / {max}
                    {pct !== null ? (
                      <span className="ml-1.5 text-ink-faint">{pct}%</span>
                    ) : null}
                  </span>
                ) : (
                  <span className="text-ink-faint">
                    {sat(exam) ? "Not released" : "—"}
                  </span>
                )}
              </TD>
              <TD>
                <PastStatusBadge status={exam.attempt?.status ?? null} />
              </TD>
              <TD align="right">
                {exam.attempt && sat(exam) ? (
                  <Link
                    to={`/student/exam/${exam.attempt.id}/result`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
                  >
                    <BarChart3 className="size-3.5" />
                    Result
                  </Link>
                ) : null}
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

/**
 * A row lands in Past once its window closes, whether or not the student ever
 * sat it — so "no attempt" and "started, never submitted" are real states here
 * and must not read as Submitted.
 */
function PastStatusBadge({
  status,
}: {
  status: AttemptStatus | null;
}) {
  switch (status) {
    case "submitted":
      return <Badge tone="answered">Submitted</Badge>;
    case "auto_submitted":
      return <Badge tone="review">Auto-submitted</Badge>;
    // Its clock ran out before the student submitted; opening the result
    // settles it as auto-submitted, so say what actually happened.
    case "in_progress":
      return <Badge tone="review">Time up</Badge>;
    case "not_started":
    case null:
      return <Badge tone="neutral">Missed</Badge>;
  }
}

/** Whether the student actually sat this paper, so a score is pending. */
function sat(exam: StudentExamSummary): boolean {
  const status = exam.attempt?.status;
  return status !== undefined && status !== "not_started";
}

function formatWhen(ms: number | null): string {
  if (ms === null) return "Not scheduled";
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
